import { type TFunction } from 'react-i18next';
import { FIELD_UPDATED_MODES } from './contants';
import { Field, GenericOption } from 'pages/AutomationEngine/types';
import type { FieldUpdatedMode } from './types';
import { FIELD_TYPES } from 'utility/constants';

export const getFieldUpdatedModeOptions = (
  field: Field | null,
  isFromValue: boolean,
  t: TFunction
) => {
  if (!field) {
    return [];
  }

  const isSpecificValueSupported = ![FIELD_TYPES.Files.type].includes(
    field.fieldType
  );
  const isDate = [FIELD_TYPES.Date.type, FIELD_TYPES.DateTime.type].includes(
    field.fieldType
  );
  const isMulty =
    field.fieldType === FIELD_TYPES.Relationship.type
      ? !['primary', 'one_to_one'].includes(field.relation.relationType)
      : field.properties.allowMultiple;

  const options: GenericOption<FieldUpdatedMode>[] = [
    {
      value: FIELD_UPDATED_MODES.ANY_INCLUDING_BLANK,
      label: t('Any Value (including Blank)'),
    },
    {
      value: FIELD_UPDATED_MODES.ANY_EXCLUDING_BLANK,
      label: t('Any Value (excluding Blank)'),
    },
    {
      value: FIELD_UPDATED_MODES.BLANK,
      label: t('Blank'),
    },
  ];

  if (!isSpecificValueSupported) {
    return options;
  }

  if (isMulty) {
    options.push(
      {
        value: FIELD_UPDATED_MODES.CONTAINS,
        label: t('Contains'),
      },
      {
        value: FIELD_UPDATED_MODES.DOES_NOT_CONTAIN,
        label: t('Does Not Contain'),
      }
    );
    if (!isFromValue) {
      options.push(
        {
          value: FIELD_UPDATED_MODES.VALUES_ADDED,
          label: t('Value(s) Added'),
        },
        {
          value: FIELD_UPDATED_MODES.VALUES_REMOVED,
          label: t('Value(s) Removed'),
        }
      );
    }
  } else {
    options.push(
      isDate
        ? {
            value: FIELD_UPDATED_MODES.DATE_BETWEEN,
            label: t('Date Between'),
          }
        : {
            value: FIELD_UPDATED_MODES.SPECIFIC_VALUE,
            label: t('Specific Value'),
          }
    );
  }

  return options;
};

const isNotInt = (value: string) => isNaN(parseInt(value));
const parseNumberOrNull = (value: string) =>
  isNotInt(value) ? null : parseFloat(value);
const parseIntOrNull = (value: string) =>
  isNotInt(value) ? null : parseInt(value);

export const getValueForApi = (field?: Field, value?: any) => {
  if (!field || (!value && value !== 0 && value !== false)) {
    return null;
  }

  switch (field.fieldType) {
    case FIELD_TYPES.Text.type:
    case FIELD_TYPES.LongText.type:
      return value || '';

    case FIELD_TYPES.Checkbox.type:
      return !!value;

    case FIELD_TYPES.Decimal.type:
    case FIELD_TYPES.Money.type:
    case FIELD_TYPES.Integer.type:
      return parseNumberOrNull(value);

    case FIELD_TYPES.Rating.type:
      return parseIntOrNull(value);

    case FIELD_TYPES.Dropdown.type:
    case FIELD_TYPES.Radio.type:
    case FIELD_TYPES.Status.type:
    case FIELD_TYPES.TeamSelector.type:
    case FIELD_TYPES.Relationship.type:
    case FIELD_TYPES.YesNoMaybe.type:
      return value?.value || null;

    default:
      return value || null;
  }
};

export const getValueForFlow = (field?: Field | null, value?: any) => {
  if (!field || (!value && value !== 0 && value !== false)) {
    return null;
  }

  switch (field.fieldType) {
    case FIELD_TYPES.Text.type:
    case FIELD_TYPES.LongText.type:
    case FIELD_TYPES.Email.type:
    case FIELD_TYPES.PhoneNumber.type:
    case FIELD_TYPES.Timezone.type:
    case FIELD_TYPES.Rating.type:
    case FIELD_TYPES.Decimal.type:
    case FIELD_TYPES.Money.type:
    case FIELD_TYPES.Integer.type:
      return value ? value.value : null;

    case FIELD_TYPES.Checkbox.type:
      return value?.value || false;

    case FIELD_TYPES.Dropdown.type:
    case FIELD_TYPES.Radio.type:
    case FIELD_TYPES.Status.type:
    case FIELD_TYPES.TeamSelector.type:
    case FIELD_TYPES.Relationship.type:
    case FIELD_TYPES.YesNoMaybe.type:
      return value
        ? {
            id: value.id,
            value: value.id,
            label: value.displayName || value.name,
            deleted: value.deleted,
          }
        : null;

    default:
      return value || null;
  }
};

export const getMultiValueForApi = (field?: Field, values?: any[]) => {
  if (!field || !values || values.length === 0) {
    return null;
  }
  switch (field.fieldType) {
    default:
      return values.map(({ value, id }) => id || value);
  }
};

export const getMultiValueForFlow = (field?: Field | null, values?: any[]) => {
  if (!field || !values || values.length === 0) {
    return null;
  }

  return values.map(({ id, name, displayName, deleted }) => ({
    value: id,
    label: displayName || name,
    deleted,
  }));
};

export const getValueLabelByMode = (
  mode: FieldUpdatedMode,
  t: TFunction,
  isFromValue?: boolean
) => {
  switch (mode) {
    case FIELD_UPDATED_MODES.CONTAINS:
      return t('Contains Any Of');
    case FIELD_UPDATED_MODES.DOES_NOT_CONTAIN:
      return t('Contains None Of');
    case FIELD_UPDATED_MODES.VALUES_ADDED:
      return t('Added Any Of');
    case FIELD_UPDATED_MODES.VALUES_REMOVED:
      return t('Removed Any Of');
    default:
      return isFromValue ? t('From Value') : t('Updated Value');
  }
};
